// This module behaves as a singleton for twirp generated API Services
import { User as OidcUser } from 'oidc-client-ts';

import {
  AdminAliasServiceImpl,
  AdminAppServiceImpl,
  AdminAuthServiceImpl,
  AdminFederationServiceImpl,
  AdminPreconditionServiceImpl,
  AdminSecretsServiceImpl,
  AdminTagServiceImpl,
  AppService as IAppService,
  AppServiceImpl,
  AuthServiceImpl,
  EnvServiceImpl,
  ImportAppRequest,
  ImportAppResponse,
  SecretsServiceImpl,
  TagServiceImpl,
} from '@src/gen/ai/h2o/cloud/appstore/v1/ai.h2o.cloud.appstore';
import type { RequestConfig, RPC } from '@src/gen/runtime';
import { createTwirpRequest, throwTwirpError, TwirpErrorJSON } from '@src/gen/twirp';
import { CANCEL_WEBSOCKET_DOWNLOAD } from '@shared-legacy/lib/utils';
import { LogLine } from '@shared-legacy/ui-kit';
import { AdminCenterServiceImpl } from '@features-legacy/admin-center/api';
import { EngineProfilesServiceImpl } from '@features-legacy/aiem/api';
import { AuditTrailServiceImpl } from '@features-legacy/audit-trail/api/api';
import { MLOpsServiceImpl } from '@features-legacy/mlops/api';
import { SecureStoreServiceImpl } from '@features-legacy/secure-store/api/api';
import { TelemetryServiceImpl } from '@features-legacy/telemetry/api';
import { LocalStorageKeys } from '@shared/lib/utils/localStorage';
import { GrpcGatewayErrorResponse } from '@shared/services/appstore/HTTPResponse';

export interface InterruptibleCall<T> {
  cancel: () => void;
  promise: Promise<T>;
}

export enum ServiceResponse {
  abort = 'abort',
  inProgress = 'inProgress',
}

export interface UploadAppResponse {
  filename: string;
}

class UploadServiceImpl {
  upload(
    file: File,
    accessToken: string | undefined,
    onProgress?: (e: ProgressEvent<EventTarget>) => void
  ): InterruptibleCall<UploadAppResponse> {
    const formData = new FormData(),
      xhr = new XMLHttpRequest();
    let state = ServiceResponse.inProgress;

    formData.append('file', file);
    const makeRequest = new Promise<UploadAppResponse>((resolve, reject) => {
      xhr.open('POST', '/v1/upload');
      if (accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      }
      xhr.upload.onprogress = onProgress || null;
      xhr.send(formData);
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== XMLHttpRequest.DONE) return;
        if (xhr.status === 0 && state === ServiceResponse.abort) {
          reject(state);
        } else if (xhr.status < 200 || xhr.status >= 300) {
          reject(xhr);
        }
      };
      xhr.onload = () => resolve(JSON.parse(xhr.response) as UploadAppResponse);
    });

    return {
      cancel: () => {
        state = ServiceResponse.abort;
        xhr.abort();
      },
      promise: makeRequest,
    };
  }
}

class DownloadServiceImpl {
  private async download(urlPrefix: string, id: string, fileName: string, accessToken: string | undefined) {
    return fetch(`${urlPrefix}${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then(async (resp) => {
      if (resp.ok) {
        const blob = await resp.blob();
        const newBlob = new Blob([blob], { type: 'application/octet-stream' });
        const data = window.URL.createObjectURL(newBlob);
        const link = document.createElement('a');
        link.href = data;
        link.download = fileName;
        link.dispatchEvent(new MouseEvent('click'));
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(data);
        }, 60);
      }
      return resp;
    });
  }

  async downloadAdminApp(id: string, fileName: string, accessToken: string | undefined) {
    return this.download('/v1/admindownload/', id, fileName, accessToken);
  }

  async downloadApp(id: string, fileName: string, accessToken: string | undefined) {
    return this.download('/v1/download/', id, fileName, accessToken);
  }
}

interface IDecoratedAppService extends IAppService {
  interruptibleImport: (data: ImportAppRequest, headers?: object) => InterruptibleCall<ImportAppResponse>;
}

class DecoratedAppService extends AppServiceImpl implements IDecoratedAppService {
  public interruptibleImport(params: ImportAppRequest, headers: object = {}): InterruptibleCall<ImportAppResponse> {
    let state = ServiceResponse.inProgress;
    const controller = new AbortController(),
      { signal } = controller,
      promise = this.fetch(this.url('ImportApp'), {
        ...createTwirpRequest(params, headers),
        signal,
      })
        .then((res) => (!res.ok ? throwTwirpError(res) : res.json()))
        .catch((error) => {
          throw state === ServiceResponse.abort ? state : error;
        });
    return {
      cancel: () => {
        state = ServiceResponse.abort;
        controller.abort();
      },
      promise: promise as Promise<ImportAppResponse>,
    };
  }
}

// Re-export constants for backward compatibility
export { LocalStorageKeys } from '@shared/lib/utils/localStorage';

export const getAccessToken = (): string => {
  const oidcKey = window.localStorage.getItem(LocalStorageKeys.OIDC_KEY);
  if (oidcKey) {
    const user = window.localStorage.getItem(oidcKey);
    if (user) {
      const userObj = JSON.parse(user) as unknown as OidcUser;
      return userObj?.access_token;
    }
  }
  return '';
};

export interface WebSocketParams {
  follow?: boolean;
  tailLines?: number;
  previous?: boolean; // currently unsupported
  workspace?: string;
  parent?: string;
}

export interface IGetWebSocketParamsWithQuery extends IGetWebSocketParams {
  queryParameters?: WebSocketParams;
}

export interface IGetWebSocketParams {
  url: URL;
}

class WebSocketServiceImpl {
  public getSocket(params: IGetWebSocketParamsWithQuery): WebSocket {
    const { url, queryParameters } = params;
    const loc = window.location;
    const hostnameString = url.hostname;
    // for mocking during development
    const portString = import.meta.env.VITE_USE_MOCK_SERVER ? ':4000' : url.port ? `:${url.port}` : '';
    let wsURL: string;
    // just in case
    if (loc.protocol === 'https:') {
      wsURL = 'wss:';
    } else {
      wsURL = 'ws:';
    }
    wsURL += `//${hostnameString}${portString}${url.pathname}`;

    if (queryParameters) {
      wsURL += '?';
      wsURL += Object.entries(queryParameters)
        .map(([key, value]) => `${key}=${value.toString()}`)
        .join('&');
    }
    const accessToken = getAccessToken();
    return accessToken ? new WebSocket(wsURL, ['access_token', accessToken]) : new WebSocket(wsURL);
  }
}

const saveStringAsZip = (fileName: string, content: string): Promise<void> => {
  return Promise.all([import('jszip'), import('file-saver')]).then(([{ default: JSZip }, { default: FileSaver }]) => {
    const zip = new JSZip();
    zip.file(`${fileName}.txt`, content);
    return zip.generateAsync({ type: 'blob' }).then((blob) => {
      FileSaver.saveAs(blob, `${fileName}.zip`);
    });
  });
};

export const instanceLogDownload = (
  websocketParams: IGetWebSocketParams,
  fileName: string,
  previous: boolean,
  successCallback?: () => any,
  failureCallBack?: (err: any) => any,
  tailLines?: number,
  scopeQueryParam?: Record<string, string>
): WebSocket | undefined => {
  if (!websocketParams || !fileName) {
    return;
  }
  let queryParameters: Record<string, any> = { follow: false, previous, ...scopeQueryParam };
  if (tailLines) {
    queryParameters = { ...queryParameters, tailLines };
  }

  const ws = WebSocketService.getSocket({
    ...websocketParams,
    queryParameters,
  });
  const _log: LogLine[] = [];
  let isSocketError = false;
  ws.addEventListener('message', (logLine) => {
    _log.push(logLine);
  });
  ws.addEventListener('close', (reason) => {
    if (reason.reason === CANCEL_WEBSOCKET_DOWNLOAD || isSocketError) {
      return;
    }
    saveStringAsZip(fileName, _log.map((line) => line.data).join('')).then(successCallback, failureCallBack);
  });
  ws.addEventListener('error', (err) => {
    if (failureCallBack) {
      isSocketError = true;
      failureCallBack(err);
    }
    ws.close();
  });
  return ws;
};

const tokenExpiredEventTarget = new EventTarget();
/**
 * This function allows any component to listen for expired-token event.
 */
export const listenTokenExpired = (c: (response: Response) => void) => {
  const listener = (event: CustomEvent<Response>) => {
    c(event.detail);
  };
  tokenExpiredEventTarget.addEventListener(`tokenExpired`, listener as any);
  return () => {
    tokenExpiredEventTarget.removeEventListener(`tokenExpired`, listener as any);
  };
};
const dispatchTokenExpired = (response: Response) => {
  tokenExpiredEventTarget.dispatchEvent(new CustomEvent(`tokenExpired`, { detail: response }));
};

export const fetchWrapRPC = <RequestMessage, ResponseMessage>(
  rpc: RPC<RequestMessage, ResponseMessage>,
  requestConfig: RequestConfig
) => {
  return async (variables: RequestMessage, { signal }: { signal?: AbortSignal } = {}) => {
    const request = rpc.createRequest(
      {
        ...requestConfig,
        bearerToken: `bearerToken` in requestConfig ? requestConfig.bearerToken : getAccessToken(),
      },
      variables
    );
    const response = await fetch(request, { signal });
    if (response.ok) {
      return (await response.json()) as Promise<ResponseMessage>;
    }
    if (response.headers.has(`Content-Type`) && response.headers.get(`Content-Type`)?.includes(`application/json`)) {
      const errorJSON = (await response.json()) as GrpcGatewayErrorResponse;
      if (response.status === 401 && errorJSON.message === `cannot resolve the caller`) {
        dispatchTokenExpired(response);
      }
      throw errorJSON;
    }
    throw response;
  };
};

export const fetchWrapRPCRawResponse = <RequestMessage, ResponseMessage>(
  rpc: RPC<RequestMessage, ResponseMessage>,
  requestConfig: RequestConfig
) => {
  return async (variables: RequestMessage, { signal }: { signal?: AbortSignal } = {}) => {
    const request = rpc.createRequest(
      {
        ...requestConfig,
        bearerToken: `bearerToken` in requestConfig ? requestConfig.bearerToken : getAccessToken(),
      },
      variables
    );
    const response = await fetch(request, { signal });
    if (response.ok) {
      return response;
    }
    const error = await response.json();
    return Promise.reject(error);
  };
};

export const patchedFetch: typeof globalThis.fetch = (path, requestInit) => {
  const accessToken = getAccessToken();
  const headers = new Headers(requestInit?.headers);
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return window
    .fetch(path, {
      ...requestInit,
      headers,
    })
    .then((response) => {
      if (response.status === 401) {
        // if the user session times-out on the server, we need to redirect the user to the login page.
        // Not all 401 responses marks timed-out session, so we need to check further.
        response
          .clone() // we need to clone the response, because we will read the body and the ReadStream can be consumed only once
          .json() // parse to JSON, if the body is not JSON, we silently ignore the parsing error
          .then(
            (json) => {
              if (
                json &&
                typeof json === `object` &&
                // These tests of messages relies on an implementation detail which is not ideal, but I don't have a better solution.
                // TODO: the tail tests can be made redundant, if we would improve the fetching logic - mark all endpoints that _require_ an Authorization token and if we fail to find it, call `auth.signInSilent().catch(auth.signInRedirect)`
                ((json as TwirpErrorJSON).msg === `invalid bearer token` || // the appstore or clouddiscovery returns this when the bearer is invalid
                  (json as TwirpErrorJSON).msg === `session missing userid`) // the appstore or clouddiscovery returns this when bearer token is missing and required
              ) {
                dispatchTokenExpired(response);
              }
            },
            () => {
              // ignore JSON parsing errors
            }
          );
      }
      return response;
    });
};

export const envURL = import.meta.env.VITE_API_PATH;

export const AdminAliasService = new AdminAliasServiceImpl(envURL, patchedFetch),
  AdminAuthService = new AdminAuthServiceImpl(envURL, patchedFetch),
  AdminAppService = new AdminAppServiceImpl(envURL, patchedFetch),
  AdminFederatedAppService = new AdminFederationServiceImpl(envURL, patchedFetch),
  AdminPreconditionService = new AdminPreconditionServiceImpl(envURL, patchedFetch),
  AdminSecretsService = new AdminSecretsServiceImpl(envURL, patchedFetch),
  AdminTagService = new AdminTagServiceImpl(envURL, patchedFetch),
  AppService = new DecoratedAppService(envURL, patchedFetch),
  AuthService = new AuthServiceImpl(envURL, patchedFetch),
  DownloadService = new DownloadServiceImpl(),
  EnvService = new EnvServiceImpl(envURL, patchedFetch),
  SecretsService = new SecretsServiceImpl(envURL, patchedFetch),
  TagService = new TagServiceImpl(envURL, patchedFetch),
  TelemetryService = new TelemetryServiceImpl(envURL),
  EngineProfiles = new EngineProfilesServiceImpl(envURL),
  AdminCenterService = new AdminCenterServiceImpl(envURL),
  MLOpsService = new MLOpsServiceImpl(envURL),
  SecureStore = new SecureStoreServiceImpl(envURL),
  AuditTrailService = new AuditTrailServiceImpl(),
  UploadService = new UploadServiceImpl(),
  WebSocketService = new WebSocketServiceImpl();
